import { useSelector } from "react-redux";
import { trialExperienceDashboardPermission } from "./constants";
import { getUserType, userHasRight, useUserRole } from "./helperMethods";

/**
 * Sponsor org feature flags from Redux `loggedInUser.userDetails` (set on login in Layout).
 * Keys align with admin Settings / subscription_information: has_trial_experience, has_chat_diligent, has_auto_score, has_sme_score.
 * When absent, treat as disabled.
 */
function isTruthyOrgDetail(value) {
  return value === true || value === "true";
}

/**
 * 14025 — Composite session-level org details accessor. Reads sponsor flags from
 * `loggedInUser.userDetails` (set by Layout for sponsor users) and falls back to
 * `vendorUserDetails.userDetails` (set by Sidebar for vendor users) so a single
 * selector works regardless of role.
 * @returns {Record<string, unknown>}
 */
export function useOrgEntitlementDetails() {
  const sponsorDetails = useSelector((s) => s?.loggedInUser?.userDetails);
  const vendorDetails = useSelector((s) => s?.vendorUserDetails?.userDetails);
  if (sponsorDetails && Object.keys(sponsorDetails).length > 0) return sponsorDetails;
  return vendorDetails || {};
}

/** @param {{ userDetails?: Record<string, unknown> } | null | undefined} loggedInUser - Redux `state.loggedInUser` */
export function isOrgTrialExperienceEnabled(loggedInUser) {
  return isTruthyOrgDetail(loggedInUser?.userDetails?.has_trial_experience);
}

/** Org trial flag from an API profile object (e.g. react-query before Redux is hydrated). */
export function isOrgTrialExperienceEnabledFromProfile(sponsorProfile) {
  return isTruthyOrgDetail(sponsorProfile?.has_trial_experience);
}

/** @param {{ userDetails?: Record<string, unknown> } | null | undefined} loggedInUser */
export function isOrgChatDiligentEnabled(loggedInUser) {
  return isTruthyOrgDetail(loggedInUser?.userDetails?.has_chat_diligent);
}

/** Org flag from logged-in sponsor profile AND per-user right (SponsorRights / Redux). */
export function canUseTrialExperienceDashboard(loggedInUser, rights) {
  return (
    isOrgTrialExperienceEnabled(loggedInUser) &&
    userHasRight(rights || [], trialExperienceDashboardPermission)
  );
}

/**
 * Org flag from logged-in sponsor profile AND per-user chat access on the same payload
 * (`userDetails.chat_diligent_access`).
 */
export function canUseChatDiligent(loggedInUser) {
  const d = loggedInUser?.userDetails;
  return (
    isOrgChatDiligentEnabled(loggedInUser) && d?.chat_diligent_access === true
  );
}

/**
 * Sponsor-flavored selectors. Accept `loggedInUser` Redux slice (sponsor session-level
 * profile). Use the vendor-aware companions below when you want a single API that works
 * for sponsor + vendor + admin contexts.
 * @param {{ userDetails?: Record<string, unknown> } | null | undefined} loggedInUser
 */
export function isOrgAutoScoreEnabled(loggedInUser) {
  return isTruthyOrgDetail(loggedInUser?.userDetails?.has_auto_score);
}

/** @param {{ userDetails?: Record<string, unknown> } | null | undefined} loggedInUser */
export function isOrgSmeScoreEnabled(loggedInUser) {
  return isTruthyOrgDetail(loggedInUser?.userDetails?.has_sme_score);
}

/**
 * Composite sponsor selector: either Auto-Score or SME-Score entitlement enabled.
 * @param {{ userDetails?: Record<string, unknown> } | null | undefined} loggedInUser
 * @returns {boolean}
 */
export function isOrgRiskScoringEnabled(loggedInUser) {
  return (
    isOrgAutoScoreEnabled(loggedInUser) ||
    isOrgSmeScoreEnabled(loggedInUser)
  );
}

/**
 * 14025 — vendor-aware hooks. Read from the right slice based on which redux
 * store has the session-level profile populated. Returns booleans suitable for
 * rendering gates anywhere in the app.
 * Admin role bypass should be layered AT THE CALL SITE so these hooks stay pure.
 *
 * These hooks return true iff the org is entitled. They DO NOT factor in
 * remaining quota — use the *CanRequest* hooks below for request-time gating.
 */
export function useIsOrgAutoScoreEnabled() {
  const d = useOrgEntitlementDetails();
  return isTruthyOrgDetail(d?.has_auto_score);
}

export function useIsOrgSmeScoreEnabled() {
  const d = useOrgEntitlementDetails();
  return isTruthyOrgDetail(d?.has_sme_score);
}

/**
 * Whether the current viewer's org entitles them to risk scoring. Vendors are
 * never entitled, whatever their org's add-on flags say: published risk
 * analysis is a sponsor-and-admin surface. Callers still layer the admin
 * bypass themselves.
 */
export function useIsOrgRiskScoringEnabled() {
  // Call every hook unconditionally: a short-circuit on the truthy path would
  // skip a call and break React's hook-order rule.
  const auto = useIsOrgAutoScoreEnabled();
  const sme = useIsOrgSmeScoreEnabled();
  const userRole = useUserRole();
  return canViewRiskScoring(userRole, auto || sme);
}

/**
 * 14028 — Request-time gate. True iff the sponsor org is entitled to the
 * sub-feature AND has remaining quota. Unlimited counts as "remaining"
 * forever; a capped org with used >= quota returns false so the FE hides
 * the corresponding checkbox on the Request New VQQ form.
 */
function hasRemainingQuota(details, unlimitedKey, usedKey, quotaKey) {
  if (details?.[unlimitedKey] === true || details?.[unlimitedKey] === "true") return true;
  const used = Number(details?.[usedKey] ?? 0);
  const quota = Number(details?.[quotaKey] ?? 0);
  if (!Number.isFinite(quota) || quota <= 0) return false;
  return used < quota;
}

export function useCanRequestAutoScore() {
  const d = useOrgEntitlementDetails();
  if (!isTruthyOrgDetail(d?.has_auto_score)) return false;
  return hasRemainingQuota(d, "auto_score_unlimited", "auto_score_used", "auto_score_quota");
}

export function useCanRequestSmeScore() {
  const d = useOrgEntitlementDetails();
  if (!isTruthyOrgDetail(d?.has_sme_score)) return false;
  return hasRemainingQuota(d, "sme_score_unlimited", "sme_score_used", "sme_score_quota");
}

/**
 * Whether a viewer may see risk scoring at all. Published risk analysis is a
 * sponsor-and-admin surface: vendors get the plain question-and-answer view
 * of a completed VQQ regardless of any entitlement on their org. Admins
 * always see it; sponsors see it when their org is entitled.
 * @param {string | null | undefined} userRole - raw role string from the session
 * @param {boolean} orgEntitled - the org-level Risk Score entitlement
 * @param {boolean} [isAdmin] - Diligent admin bypass, resolved by the caller
 */
export function canViewRiskScoring(userRole, orgEntitled, isAdmin = false) {
  if (isAdmin) return true;
  if (getUserType(userRole) === "vendor") return false;
  return !!orgEntitled;
}
